import { Link } from "react-router-dom";
import { Award, Heart, ShieldCheck, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, Reveal } from "@/components/Primitives";
import { IMAGES, BUSINESS } from "@/data/site";

const VALUES = [
  { icon: ShieldCheck, title: "Trust, verified", desc: "Our 11-step screening isn't marketing — it's the foundation of everything we do." },
  { icon: Heart, title: "Warmth, always", desc: "We match caregivers who genuinely love children and treat your family like their own." },
  { icon: Award, title: "Excellence, earned", desc: "Recognized as a 'Best of Texas' agency across three decades of dependable service." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About"
        description="For over 30 years Mom's Best Friend Sitters has been DFW's most trusted premium sitter agency, matching families with warm, rigorously screened caregivers."
        breadcrumbs={[{ name: "Home", item: "/" }, { name: "About", item: "/about" }]}
      />
      <PageHero
        overline={`Est. ${BUSINESS.established}`}
        title="Three decades of trusted care in DFW"
        subtitle="Mom's Best Friend began with a simple belief: families deserve caregivers they can trust completely. Thirty years later, that belief still guides every match we make."
      />

      <section className="container-mbf py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="rounded-[2rem] overflow-hidden shadow-warm border border-[#F1E6D0]">
            <img src={IMAGES.familyMoment} alt="A warm family moment" className="w-full h-[440px] object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="overline mb-3">Our story</p>
          <h2 className="font-serif text-2xl md:text-3xl text-espresso mb-5">A boutique agency, built on relationships</h2>
          <p className="text-base text-stoneish leading-relaxed mb-4">
            We're not a faceless app. We're a boutique Dallas agency where a real person — Jess, Jade or Shannon — personally matches your family with the right caregiver and confirms every detail.
          </p>
          <p className="text-base text-stoneish leading-relaxed">
            From newborn nights to wedding days, from hotel stays to standing weekly sitters, generations of DFW families have trusted us with their most precious moments. It's a responsibility we've never taken lightly.
          </p>
        </Reveal>
      </section>

      <section className="container-mbf pb-4 md:pb-8">
        <Reveal>
          <div className="rounded-[2rem] overflow-hidden border-[6px] border-white shadow-pop ring-4 ring-gold/20">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_trusted-care-dfw/artifacts/vrieu4ml_image.png"
              alt="The Mom's Best Friend team, led by our owner, with our nannies and coordinators"
              className="w-full h-[320px] md:h-[520px] object-cover"
            />
          </div>
          <div className="mt-8 max-w-3xl">
            <p className="overline mb-3">Meet the team</p>
            <h2 className="font-serif text-2xl md:text-3xl text-espresso mb-4">Real people who know your family by name</h2>
            <p className="text-base text-stoneish leading-relaxed mb-3">
              Behind every placement is our owner and a close-knit team of coordinators and caregivers who take your family's trust personally. We're not an app or a call center — when you reach out, you get a real person who knows you.
            </p>
            <p className="text-base text-stoneish leading-relaxed">
              Your requests are looked after by <strong className="text-espresso">Jess (Families)</strong>, <strong className="text-espresso">Jade (Sitters)</strong> and <strong className="text-espresso">Shannon (Special Events)</strong> — supported by our roster of warm, 11-step screened nannies.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-parchment-2 border-y border-[#F1E6D0] mt-16 md:mt-24">
        <div className="container-mbf py-20 md:py-28">
          <div className="grid sm:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="bg-white rounded-2xl border border-[#F1E6D0] p-8 h-full" data-testid={`about-value-${i}`}>
                  <v.icon className="w-9 h-9 text-terracotta mb-4" />
                  <h3 className="font-serif text-xl text-espresso mb-2">{v.title}</h3>
                  <p className="text-sm text-stoneish leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-mbf py-20 md:py-28">
        <div className="grid sm:grid-cols-3 gap-6 text-center max-w-4xl mx-auto">
          {[["30+", "Years serving DFW"], ["11", "Step screening"], ["100%", "CPR certified"]].map(([stat, label]) => (
            <div key={label} className="bg-espresso text-parchment rounded-2xl p-8" data-testid={`about-stat-${label}`}>
              <div className="font-serif text-4xl md:text-5xl text-gold">{stat}</div>
              <div className="text-sm text-parchment/70 mt-2">{label}</div>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/book" className="btn-accent !px-8 !py-4" data-testid="about-request-cta">Experience the difference <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </>
  );
}
